import { createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '@/firebase/config';
import axios from '@/lib/axiosClient';
import '@/App.css';
import Strategies from './components/Strategies';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import { Toaster } from '@/components/ui/sonner';
import { Activity, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = '/api';

// Retained for backward-compat with the preserved (but no-longer-routed)
// intraday components, which import useRefresh. Not used by the position bot.
const RefreshContext = createContext({ refreshKey: 0 });
export const useRefresh = () => useContext(RefreshContext);

function Navigation() {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Local sign-out error:', error);
    }
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Backend logout error:', error);
    }
    // Bounce through the SSO portal so its session is also cleared — otherwise
    // the next protected-route redirect would silently sign us back in.
    const portalUrl =
      process.env.REACT_APP_LOGIN_PORTAL_URL || 'https://login.someaditya.in/';
    window.location.replace(`${portalUrl}?action=logout`);
  };

  return (
    <nav className="nav-container">
      <div className="nav-content">
        <div className="nav-brand" onClick={() => navigate('/')}>
          <Activity className="brand-icon" />
          <span className="brand-text">AlgoTrader</span>
        </div>

        <div className="nav-links">
          <NavLink
            to="/"
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            data-testid="nav-strategies"
          >
            <span>Strategies</span>
          </NavLink>
        </div>

        <div className="nav-actions">
          <Button
            onClick={handleLogout}
            variant="ghost"
            size="icon"
            data-testid="logout-btn"
            title="Sign out"
            aria-label="Sign out"
          >
            <LogOut size={16} />
          </Button>
        </div>
      </div>
    </nav>
  );
}

function AppContent() {
  return (
    <>
      <Navigation />
      <div className="main-content">
        <Routes>
          {/* Single page tracking all three momentum schemes side by side. */}
          <Route path="/" element={<Strategies />} />
          <Route path="/strategies" element={<Strategies />} />
          {/* Legacy per-strategy routes now fold into the single dashboard. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AppContent />
              </ProtectedRoute>
            }
          />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
